<template>
  <div class="modal-card modal-card--md">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <span v-if="modalScreenShowing !== 'new'">Select Bucketing</span>
        <span v-if="modalScreenShowing === 'new'">Custom Bucketing</span>
      </p>
      <a class="close-modal" aria-label="close" @click="closeModal()">
        <i class="material-icons">close</i>
      </a>
    </header>
    <section class="modal-card-body">
      <div v-if="modalScreenShowing !== 'new'">
        <p>
          Select the bucketing approach you'd like for your query.
        </p>

        <h5>
          Regulatory
        </h5>

        <div v-if="optionsData && optionsData.default_bucketing_sets">
          <div class="pseudo-selector"
            v-for="(bucket, index) of optionsData.default_bucketing_sets"
            :key="index"
            :class="{'pseudo-selector--active': typeOfBucketSelected === 'regulatory' && internalSelectedBucketing === index }"
            @click="selectBucket('regulatory', index)"
          >
            <div class="pseudo-selector__radio"></div>
            Regulatory Bucketing Approach {{ index }}
            <div class="bucket-detail">
              {{ bucket }}
            </div>
          </div>
        </div>

        <h5>
          Custom
        </h5>

        <div class="historial-buckets" v-if="customBuckets && customBuckets.length">
          <div class="historical-bucket-container">
            <div class="pseudo-selector"
              v-for="(histBucket, index) of customBuckets"
              :key="'hb_' + index"
              :class="{'pseudo-selector--active': typeOfBucketSelected === 'custom' && internalCustomSelectedBucketing === histBucket.id }"
              @click="selectBucket('custom', histBucket.id)"
            >
              <div class="pseudo-selector__radio"></div>
              {{ histBucket.name }}
              <div class="bucket-values bucket-detail">
                {{ histBucket.value }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="modalScreenShowing === 'new'">
        <p>
          Create a new custom bucket, or select one you have used previously.
        </p>

        <div class="pseudo-selector pseudo-selector--no-checkbox">
          New Custom Bucketing Approach
        </div>

        <input class="input" type="text" placeholder="Name your custom bucketing approach" v-model="customBucketName" @focus="newCustomBucketInputFocuessed()"/>

        <h6>
          Thresholds (in years)
        </h6>

        <div class="custom-inputs-row">
          <div class="input-container"
            v-for="n in bucketDividers.length"
            :key="'bd_' + n"
          >
            <input class="input" type="text" placeholder="0"
              v-model="bucketDividers[n - 1]"
              @focus="newCustomBucketInputFocuessed()"
            >
            <div class="delete-divider" @click="removeDividerAtIndex(n - 1)">
              &times;
            </div>
          </div>

          <button class="button button-primary button-icon" @click="addBucketDivider()" :disabled="bucketDividers.length >= MAX_DIVIDERS">
            <i class="material-icons">add</i>
          </button>
        </div>
        <div class="error-text custom-inputs-error">
          {{ bucketDividersError }}
        </div>
      </div>

      <div class="buttons-row"
        :class="{'buttons-row--space-between': modalScreenShowing !== 'new'}"
      >
        <button class="button button-secondary button-outline button-medium"
          @click="toggleCustomBucketingView()"
          v-if="modalScreenShowing !== 'new'"
        >
          Create Custom Bucket
        </button>
        <div>
          <button class="button button-secondary button-outline button-medium"
            @click="closeModal()"
            v-if="modalScreenShowing !== 'new'"
          >
            Cancel
          </button>
          <button class="button button-secondary button-outline button-medium"
            @click="toggleCustomBucketingView()"
            v-if="modalScreenShowing === 'new'"
          >
            Back
          </button>
          <button class="button button-primary button-medium" :disabled="isSavedDisabled"
            @click="save()"
          >
            Save
          </button>
        </div>
      </div>

      <div class="error-text" v-if="customBucketNameError && modalScreenShowing === 'new'"> <!-- && internalCustomSelectedBucketing === 'new' -->
        {{ customBucketNameError }}
      </div>

    </section>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import GAService from '@/services/ga.service';
import { Bucket } from '@/store/types';
import _ from 'lodash';

@Component({
  components: {
  },
})
export default class SelectBucketingModal extends Vue {

  public internalSelectedBucketing: string = '';
  public internalCustomSelectedBucketing: string = '';
  public modalScreenShowing: 'select' | 'new' = 'select';
  public typeOfBucketSelected: 'custom' | 'regulatory' = 'regulatory';

  public MAX_DIVIDERS: number = 12;
  public customBucketName: string = '';
  public bucketDividers: string[] = ['0'];
  public bucketDividersError: string = '';
  public customBucketNameError: string = '';

  public get attributeIdUnderEdit(): string | undefined {
    return this.$store.getters.getAttributeIdUnderEdit;
  }

  public get optionsData() {
    return this.$store.state.options.options || {};
  }

  get customBuckets() {
    if (this.$store.state.buckets.customBuckets) {
      return _.orderBy(this.$store.state.buckets.customBuckets, ['id'], ['desc']);
    }

    return [];
  }

  get activeBucket(): Bucket {
    return this.$store.getters.getBucketingForAttribute(this.attributeIdUnderEdit);
  }

  get isSavedDisabled() {
    if (!this.internalSelectedBucketing) {
      return true;
    } else if (this.modalScreenShowing === 'new') {
      return !!this.bucketDividersError;
    }
    return false;
  }

  @Watch('bucketDividers')
  private onBucketDividersChange(value: string[], oldValue: string[]) {
    let error = '';
    let lastNumber: number = 0;
    for (let i = 0; i < value.length; i++) {
      if (!this.isNumeric(value[i])) {
        error = 'Bucket divider values must be numeric';
        break;
      } else {
        const numeric = parseFloat(value[i]);
        if (i > 0) {
          if (numeric <= lastNumber) {
            error = 'Bucket divider values must increase';
            break;
          }
        }
        lastNumber = numeric;
      }
    }
    this.bucketDividersError = error;
  }

  @Watch('customBucketName')
  private onCustomBucketNameChange(value: string, oldValue: string) {
    if (!!value) {
      this.customBucketNameError = '';
    }
  }

  public selectBucket(typeOfBucket: 'custom' | 'regulatory', id: string) {
    this.typeOfBucketSelected = typeOfBucket;
    if (typeOfBucket === 'custom') {
      this.internalCustomSelectedBucketing = id;
    } else {
      this.internalSelectedBucketing = id;
    }
  }

  public isNumeric(value: any): boolean {
    return !isNaN(value - parseFloat(value));
  }

  public closeModal() {
    this.$store.commit('setModal', '');
  }

  public save() {
    let bucket: Bucket;
    const setBucketing = (): void => {
      this.$store.commit('setSelectedBucketing', { id: this.attributeIdUnderEdit, bucket });
    };

    if (this.modalScreenShowing === 'new') {
      if (!this.customBucketName) {
        this.customBucketNameError = 'You must name your custom bucket';
        return;
      }

      bucket = {
        type: 'custom',
        id: Date.now().toString(),
        name: this.customBucketName,
        value: this.bucketDividers.map( (str: string) => parseFloat(str))
      };
      this.$store.dispatch('saveCustomBucket', bucket);
      GAService.sendEvent(this.$gtag, 'Bucket', 'Custom Created', bucket.name as string);
      setBucketing();
    } else if (this.typeOfBucketSelected === 'regulatory') { // TODO: differentiate between custom and regulatory
      bucket = {
        type: 'regulatory',
        id: this.internalSelectedBucketing,
        value: this.optionsData.default_bucketing_sets[this.internalSelectedBucketing]
      };
      GAService.sendEvent(this.$gtag, 'Bucket', 'Regulatory Selected', this.internalSelectedBucketing);
      setBucketing();
    } else if (this.typeOfBucketSelected === 'custom') {
      bucket = this.customBuckets.find( (custBucket: any) => {
        return this.internalCustomSelectedBucketing === custBucket.id;
      }) || {};

      GAService.sendEvent(this.$gtag, 'Bucket', 'Custom Selected', bucket.name as string);
      setBucketing();
    }

    this.$store.commit('setAttributeIdUnderEdit', undefined);
    this.closeModal();
  }

  public toggleCustomBucketingView() {
    this.modalScreenShowing = this.modalScreenShowing === 'new' ? 'select' : 'new';
    // GAService.sendEvent(this.$gtag, 'Bucket', 'Window Toggled', this.customBucketingView ? 'Custom' : 'Main'); // TODO: look at
  }

  public addBucketDivider() {
    if (this.bucketDividers.length < this.MAX_DIVIDERS) {
      //this.internalCustomSelectedBucketing = 'new';
      const newNumber = parseFloat(this.bucketDividers[this.bucketDividers.length - 1]) + 1 + '';
      this.bucketDividers = this.bucketDividers.concat(newNumber);
    }
  }

  public removeDividerAtIndex(index: number) {
    if (this.bucketDividers.length > 1) {
      const arr = this.bucketDividers.slice();
      arr.splice(index, 1);
      this.bucketDividers = arr;
    } else {
      this.bucketDividers = ['0'];
    }
  }

  public newCustomBucketInputFocuessed() {
    //this.internalCustomSelectedBucketing = 'new';
  }

  public mounted() {
    GAService.sendEvent(this.$gtag, 'Bucket', 'Window Opened', 'Main');

    if (this.activeBucket && !_.isEmpty(this.activeBucket)) {
      if (this.activeBucket.type === 'regulatory') {
        this.typeOfBucketSelected = 'regulatory';
        this.internalSelectedBucketing = this.activeBucket.id;
      } else {
        this.typeOfBucketSelected = 'custom';
        this.internalSelectedBucketing = 'custom';
      }

      if (this.activeBucket.type === 'custom') {
        this.internalCustomSelectedBucketing = this.activeBucket.id;
      }
    } else {
      this.$store.commit('setToastError', 'Error: Failed to find bucket for editing');
      this.closeModal();
    }
  }
}

</script>

<style scoped lang="scss">
@import '@/scss/partials/_settings.colors.scss';
.modal .modal-card {
  .pseudo-selector {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 10px;
    border-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;

    .pseudo-selector__checkbox {
      top: 3px;
      left: 0;
    }

    &--no-checkbox {
      padding-left: 0;
    }

    .bucket-detail {
      font-size: 12px;
      font-weight: 400;
    }

    .pseudo-selector__radio--disabled {
      border-color: $grey-240;
    }

    &:hover {
      cursor: pointer;
      background-color: transparent;
    }
  }

  .custom-bucketing-row {
    border: 1px solid red;
    margin-top: 10px;
    text-align: left;

    &:hover {
      cursor: pointer;
    }
  }

  h5 {
    margin-bottom: 10px;
  }

  h6 {
    margin-top: 10px;
    font-size: 14px;
  }

  .custom-inputs-row {
    display: flex;
    margin-top: 10px;

    .input-container {
      position: relative;
      margin-right: 5px;

      .delete-divider {
        position: absolute;
        right: 3px;
        top: 3px;
        bottom: 3px;
        font-size: 20px;
        line-height: 24px;
        padding: 0 3px;
        background-color: rgba(255, 255, 255, .9);
        display: none;

        &:hover {
          cursor: pointer;
          color: $refinitiv-blue;
        }
      }

      &:hover {
        .delete-divider {
          display: block;
        }

        input:focus ~ .delete-divider {
          display: none;
        }

      }


    }
    input {
      flex-grow: 0;
      max-width: 50px;
      height: 30px;
    }

    .button {
      height: 30px;
      line-height: 30px;
      padding: 0;
      width: 30px;

      i {
        margin-left: 0;
        font-size: 20px;
      }
    }
  }

  .error-text {
    font-size: 12px;
    color: $red;
    text-align: right;
    font-weight: 500;
    margin-top: 3px;
  }

  .custom-inputs-error {
    text-align: left;
  }

  .historial-buckets {
    margin-top: 20px;

    h5 {
      margin-bottom: 10px;
    }

    .bucket-values {
      font-weight: 400;
    }

    .historical-bucket-container {
      max-height: 212px;
      overflow-y: scroll;
      border: 1px solid $grey-220;

      .pseudo-selector {
        padding-top: 10px;
        padding-bottom: 10px;
        padding-right: 10px;
        padding-left: 40px;
        margin-bottom: 0;

        .pseudo-selector__radio {
          top: 13px;
          left: 10px;
        }
      }

      .pseudo-selector:nth-child(2n - 1) {
        background-color: #f6f7f8;
      }
    }
  }

  .buttons-row--space-between {
    display: flex;
    justify-content: space-between;
  }

  .delete-bucket {
    position: absolute;
    right: 10px;
    top: 0;
    bottom: 0;
    z-index: 1;
    padding: 0 10px 0 30px;
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;
      color: $refinitiv-blue;
      font-weight: 600;
    }
  }
}
</style>
